import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import LegalDocumentLanguageToggle from '../components/LegalDocumentLanguageToggle';
import { termsOfUseJsonUrl } from '../config/termsOfUse';
import {
    type DocumentLocale,
    persistDocumentLocale,
    resolveDocumentLocale,
} from '../utils/documentLocale';
import { getLegalDocumentChrome } from '../utils/legalDocumentChrome';
import {
    fetchLegalDocument,
    type LegalDocument,
} from '../utils/legalDocumentTypes';
import { renderPolicyBlock } from '../utils/renderPolicyBlock';
import './TermsOfUsePage.css';

const TermsOfUsePage: React.FC = () => {
    const { version } = useParams<{ version: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [locale, setLocale] = useState<DocumentLocale>(() =>
        resolveDocumentLocale(searchParams)
    );
    const [document, setDocument] = useState<LegalDocument | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const chrome = getLegalDocumentChrome(locale);

    const handleLocaleChange = useCallback(
        (nextLocale: DocumentLocale) => {
            persistDocumentLocale(nextLocale);
            setLocale(nextLocale);

            const nextParams = new URLSearchParams(searchParams);
            nextParams.set('lang', nextLocale);
            setSearchParams(nextParams, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        const resolvedLocale = resolveDocumentLocale(searchParams);
        setLocale(resolvedLocale);
    }, [searchParams]);

    useEffect(() => {
        if (!version) {
            setError(chrome.versionNotSpecified);
            setIsLoading(false);
            return;
        }

        let isCancelled = false;

        const loadTerms = async () => {
            setIsLoading(true);
            setError(null);
            setDocument(null);

            try {
                const localizedUrl = termsOfUseJsonUrl(version, locale);
                const fallbackUrl = termsOfUseJsonUrl(version, 'tr');
                const data = await fetchLegalDocument<LegalDocument>(
                    localizedUrl,
                    fallbackUrl
                );

                if (!isCancelled) {
                    setDocument(data);
                }
            } catch {
                if (!isCancelled) {
                    setError(chrome.termsOfUseLoadFailed);
                }
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadTerms();

        return () => {
            isCancelled = true;
        };
    }, [version, locale, chrome.termsOfUseLoadFailed, chrome.versionNotSpecified]);

    if (isLoading) {
        return (
            <div className="terms-of-use-page">
                <div className="container">
                    <div className="page-header">
                        <h1>{chrome.termsOfUseTitle}</h1>
                        <p className="last-updated">{chrome.loading}</p>
                        <LegalDocumentLanguageToggle
                            locale={locale}
                            onChange={handleLocaleChange}
                        />
                    </div>
                </div>
            </div>
        );
    }

    if (error || !document) {
        return (
            <div className="terms-of-use-page">
                <div className="container">
                    <div className="page-header">
                        <h1>{chrome.termsOfUseTitle}</h1>
                        <p className="last-updated">
                            {error ?? chrome.termsOfUseLoadFailed}
                        </p>
                        <LegalDocumentLanguageToggle
                            locale={locale}
                            onChange={handleLocaleChange}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="terms-of-use-page">
            <div className="container">
                <div className="page-header">
                    <h1>{document.title}</h1>
                    <p className="last-updated">
                        {chrome.lastUpdated} {document.lastUpdated}
                    </p>
                    <LegalDocumentLanguageToggle
                        locale={locale}
                        onChange={handleLocaleChange}
                    />
                </div>

                <div className="policy-content">
                    {document.sections.map((section, sectionIndex) => (
                        <section key={sectionIndex} className="policy-section">
                            <h2>{section.title}</h2>
                            {section.blocks.map((block, blockIndex) =>
                                renderPolicyBlock(block, blockIndex)
                            )}
                        </section>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TermsOfUsePage;
